<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { clinicalDrugsApi, type ClinicalDrug } from '@/api/clinical-drugs';
import type { MedicationOrder } from '@/api/medications';
import { ROUTES, routeLabel } from '@/utils/medications';

// First step of a new order: the drug and its route, with the orders the
// patient already has of it, or of another drug with the same active
// ingredient. The same drug is changed in place; another one is suspended by
// hand after saving.
defineProps<{ duplicates: MedicationOrder[]; showErrors: boolean }>();
const emit = defineEmits<{ changeOrder: [order: MedicationOrder] }>();
const drug = defineModel<ClinicalDrug | null>('drug', { required: true });
const route = defineModel<string>('route', { required: true });

const SEARCH_DELAY_MS = 300;
const MIN_SEARCH_LENGTH = 3;

const search = ref('');
const term = ref('');
let searchTimer: ReturnType<typeof setTimeout> | undefined;
// Picking a drug writes its name in the field: that is not a new search.
watch(search, (value) => {
  clearTimeout(searchTimer);
  if (drug.value && value === drug.value.displayName) return;
  searchTimer = setTimeout(() => {
    term.value = (value ?? '').trim();
  }, SEARCH_DELAY_MS);
});
onBeforeUnmount(() => clearTimeout(searchTimer));

const searchable = computed(() => term.value.length >= MIN_SEARCH_LENGTH);
const { data: drugs, isFetching } = useQuery({
  queryKey: ['clinicalDrugs', term],
  queryFn: () => clinicalDrugsApi.search(term.value),
  enabled: searchable,
  initialData: [],
});

// While typing or loading, say so instead of flashing "no results".
const typed = computed(() => (search.value ?? '').trim());
const noDataText = computed(() => {
  if (typed.value === drug.value?.displayName) return '';
  if (typed.value.length < MIN_SEARCH_LENGTH) return 'Escribe al menos 3 letras';
  if (isFetching.value || typed.value !== term.value) return 'Buscando...';

  return `No encontramos «${typed.value}». Pídelo a soporte.`;
});

function sameDrug(order: MedicationOrder): boolean {
  return order.clinicalDrugId === drug.value?.id;
}

function title(order: MedicationOrder): string {
  const status = order.periodStatus === 'upcoming' ? 'programada' : 'vigente';

  return sameDrug(order) ?
    `Ya tiene esta indicación ${status}` :
    `Ya tiene ${order.drugName} ${status}, con el mismo principio activo`;
}
</script>
<template>
  <div class="d-flex flex-column ga-4">
    <v-autocomplete
      v-model:search="search"
      v-model="drug"
      :items="searchable ? drugs : []"
      :loading="isFetching"
      :no-data-text="noDataText"
      item-title="displayName"
      return-object
      no-filter
      autofocus
      clearable
      menu-icon=""
      prepend-inner-icon="mdi-magnify"
      placeholder="Nombre, marca o principio activo"
      aria-label="Buscar medicamento"
      variant="outlined"
      :error-messages="showErrors && !drug ? 'Elige un medicamento' : ''"
      :hide-details="!(showErrors && !drug)"
    >
      <template #item="{ props: itemProps, item }">
        <v-list-item v-bind="itemProps">
          <template #append>
            <span class="text-caption text-medium-emphasis ml-4">{{ routeLabel(item.raw.defaultRoute) }}</span>
          </template>
        </v-list-item>
      </template>
    </v-autocomplete>

    <v-select
      v-if="drug"
      v-model="route"
      :items="ROUTES"
      label="Vía"
      variant="outlined"
      density="compact"
      hide-details
      class="route-field"
    />

    <v-alert
      v-for="order in duplicates"
      :key="order.id"
      type="warning"
      variant="tonal"
      density="compact"
      :title="title(order)"
    >
      <div class="text-body-2">
        {{ order.dosages.map((dosage) => dosage.summary).join(' · ') }}
      </div>
      <v-btn
        v-if="sameDrug(order)"
        color="warning"
        variant="flat"
        size="small"
        class="mt-2"
        @click="emit('changeOrder', order)"
      >
        Cambiar la indicación actual
      </v-btn>
      <div
        v-else
        class="text-body-2 text-medium-emphasis mt-1"
      >
        Si esta la reemplaza, suspende esa después de guardar.
      </div>
    </v-alert>
  </div>
</template>
<style scoped>
.route-field {
  max-width: 280px;
}
</style>
